import { useState } from "react";
import { KeyRound, Save } from "lucide-react";
import { toast } from "sonner";
import { authApi } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminAccount() {
  const { admin, setSession } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Enter your current password to authorise this change");
      return;
    }
    if (!newEmail && !newPassword) {
      toast.error("Change either the email or the password");
      return;
    }
    if (newPassword && newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (newEmail) payload.new_email = newEmail;
      if (newPassword) payload.new_password = newPassword;
      const { data } = await authApi.put("/auth/change-credentials", payload);
      // Persist the fresh JWT so the session survives an email change
      if (data.access_token) {
        localStorage.setItem("ping_admin_token", data.access_token);
      }
      if (setSession && data.user) setSession(data.user, data.access_token);
      const bits = [];
      if (data.email_changed) bits.push("email");
      if (data.password_changed) bits.push("password");
      toast.success(`Updated ${bits.join(" & ")} — session refreshed`);
      setCurrentPassword("");
      setNewEmail("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-account-page">
      <div className="flex items-center gap-3 mb-2">
        <KeyRound className="h-6 w-6 text-[#E31837]" />
        <h1 className="heading-2">Account &amp; Password</h1>
      </div>
      <p className="text-muted-foreground text-sm mb-8">
        Update your admin login email and password. Your current password is
        required to authorise any change.
      </p>

      <div className="bg-white border border-border p-6 mb-6">
        <p className="label-caps text-muted-foreground mb-1">Signed in as</p>
        <p className="text-lg font-semibold" data-testid="account-current-email">{admin?.email}</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-border p-6 space-y-5">
        <div>
          <Label className="label-caps text-muted-foreground">Current Password *</Label>
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="mt-2 rounded-none"
            data-testid="account-current-password"
            autoComplete="current-password"
          />
        </div>

        <div className="border-t border-border pt-5">
          <p className="label-caps text-muted-foreground mb-3">Change Email (optional)</p>
          <Input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={admin?.email || "new-admin@pinggolf.in"}
            className="rounded-none"
            data-testid="account-new-email"
            autoComplete="off"
          />
          <p className="text-xs text-muted-foreground mt-1">Leave blank to keep the current email.</p>
        </div>

        <div className="border-t border-border pt-5 space-y-3">
          <p className="label-caps text-muted-foreground">Change Password (optional)</p>
          <div>
            <Label className="text-xs text-muted-foreground">New Password (min 8 chars)</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 rounded-none"
              data-testid="account-new-password"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Confirm New Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 rounded-none"
              data-testid="account-confirm-password"
              autoComplete="new-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-[#E31837] text-white px-6 py-3 label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-95 disabled:opacity-60"
          data-testid="account-save-btn"
        >
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Update Credentials"}
        </button>
      </form>
    </div>
  );
}
